import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";

const BASE = "/admin/campus/";

export interface TransferSummary {
	id: number; folio: string; statusId: number; status: string;
	fromWarehouseId: number; fromWarehouse: string | null; toWarehouseId: number; toWarehouse: string | null;
	expectedAt: string | null; requested: number; sent: number; received: number; damaged: number; missing: number;
	products: string[]; lineCount: number; requestedBy: string; requestedAt: string | null;
	dispatchedAt: string | null; dispatchedBy: string | null; receivedAt: string | null; receivedBy: string | null;
	late: boolean; note: string | null; cancelReason: string | null;
}

export interface TransferDetail extends TransferSummary {
	lines: { id: number; articleId: number; name: string; waCode: string | null; barcode: string | null; serialized: boolean; requested: number; sent: number; received: number; damaged: number; missing: number; originOnHand: number; availableSerials: string[]; serials: { serial: string; status: string }[] }[];
	files: { id: number; stage: string; name: string }[];
	history: { summary: string; note: string | null; author: string; createdAt: string | null }[];
	dispatchedById: number | null;
}

export interface CountSummary {
	id: number; folio: string; statusId: number; status: string; kind: string; kindLabel: string;
	warehouseId: number; warehouse: string | null; shelves: string | null; note: string | null; lineCount: number; pieces: number;
	countedBy: string; countedById: number | null; secondCounter: string | null; secondCounterId: number | null;
	signedAt: string | null; approvedBy: string | null; approvedAt: string | null; createdAt: string | null; cancelReason: string | null;
}

export interface CountDetail extends CountSummary {
	lines: { id: number; articleId: number; name: string; waCode: string | null; barcode: string | null; serialized: boolean; counted: number; second: number | null; final: number; system: number | null; difference: number | null; unitCost: number | null; serials: string[] }[];
	files: { id: number; shelf: string | null; name: string }[];
	canEdit: boolean; canSign: boolean; canApprove: boolean;
	counters: { id: number; name: string }[];
}

/** Traslados entre bodegas (sección "transfers") y conteos de inventario (sección "stockCounts"). */
export class StockOpsService {
	private form(data: Record<string, any>, files: File[] = [], key = "photos"): FormData {
		const body = new FormData();
		Object.entries(data).forEach(([name, value]) => {
			if (value === undefined || value === null) return;
			body.append(name, typeof value === "object" ? JSON.stringify(value) : String(value));
		});
		files.forEach((file) => body.append(key + "[]", file));
		return body;
	}

	transfers = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/transfers`, { params: { ...filters, page, perPage } });
	transferCounts = async (campusId: number): Promise<AxiosResponse<{ requested: number; inTransit: number; late: number }>> =>
		axiosInstance.get(`${BASE}${campusId}/transfers/_counts`);
	transfer = async (campusId: number, id: number): Promise<AxiosResponse<TransferDetail>> =>
		axiosInstance.get(`${BASE}${campusId}/transfers/${id}`);
	createTransfer = async (campusId: number, data: Record<string, any>, dispatchNow: boolean, photos: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/transfers`, this.form({ ...data, dispatchNow: dispatchNow ? 1 : 0 }, photos));
	dispatchTransfer = async (campusId: number, id: number, lines: any[], photos: File[], note: string, expectedAt: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/transfers/${id}/_dispatch`, this.form({ lines, note, expectedAt }, photos));
	receiveTransfer = async (campusId: number, id: number, lines: any[], photos: File[], note: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/transfers/${id}/_receive`, this.form({ lines, note }, photos));
	cancelTransfer = async (campusId: number, id: number, reason: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/transfers/${id}/_cancel`, { reason });
	transferFile = async (campusId: number, id: number, fileId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/transfers/${id}/_file/${fileId}`, { responseType: "blob" });

	counts = async (campusId: number): Promise<AxiosResponse<CountSummary[]>> =>
		axiosInstance.get(`${BASE}${campusId}/stockCounts`);
	count = async (campusId: number, id: number): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.get(`${BASE}${campusId}/stockCounts/${id}`);
	createCount = async (campusId: number, data: Record<string, any>): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.post(`${BASE}${campusId}/stockCounts`, data);
	saveCountLines = async (campusId: number, id: number, lines: any[], photos: File[], shelf: string): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.post(`${BASE}${campusId}/stockCounts/${id}/_lines`, this.form({ lines, shelf }, photos));
	submitCount = async (campusId: number, id: number, secondCounterId: number): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.post(`${BASE}${campusId}/stockCounts/${id}/_submit`, { secondCounterId });
	signCount = async (campusId: number, id: number, corrections: { lineId: number; quantity: number }[]): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.post(`${BASE}${campusId}/stockCounts/${id}/_sign`, { corrections });
	applyCount = async (campusId: number, id: number, costs: { lineId: number; unitCost: number }[]): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.post(`${BASE}${campusId}/stockCounts/${id}/_apply`, { costs });
	cancelCount = async (campusId: number, id: number, reason: string): Promise<AxiosResponse<CountDetail>> =>
		axiosInstance.post(`${BASE}${campusId}/stockCounts/${id}/_cancel`, { reason });
	countFile = async (campusId: number, id: number, fileId: number): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/stockCounts/${id}/_file/${fileId}`, { responseType: "blob" });

	legacy = async (campusId: number, filters: Record<string, any>, page: number, perPage: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_legacy`, { params: { ...filters, page, perPage } });
	legacyCounts = async (campusId: number): Promise<AxiosResponse<{ pending: number; pendingWithSales: number; decided: number }>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_legacyCounts`);
	legacyExport = async (campusId: number, filters: Record<string, any>): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_legacyExport`, { params: filters, responseType: "blob" });
	legacyConvert = async (campusId: number, id: number, kind: string, name: string): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_legacyConvert/${id}`, { kind, name: name || undefined });
	legacyEquivalent = async (campusId: number, id: number, newArticleId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_legacyEquivalent/${id}`, { newArticleId });
	legacyDeprecate = async (campusId: number, ids: number[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_legacyDeprecate`, { ids });
	legacyKeep = async (campusId: number, id: number): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_legacyKeep/${id}`);
	legacyUndo = async (campusId: number, id: number): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/products/_legacyUndo/${id}`);

	exportProducts = async (campusId: number, filters: Record<string, any>): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/products/_export`, { params: filters, responseType: "blob" });
	exportStock = async (campusId: number, filters: Record<string, any>): Promise<AxiosResponse<Blob>> =>
		axiosInstance.get(`${BASE}${campusId}/warehouses/_stockExport`, { params: filters, responseType: "blob" });

	profit = async (campusId: number, filters: Record<string, any>): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_profit`, { params: filters });
	targets = async (campusId: number): Promise<AxiosResponse<Record<string, number>>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_targets`);
	saveTargets = async (campusId: number, targets: Record<string, number>): Promise<AxiosResponse<Record<string, number>>> =>
		axiosInstance.put(`${BASE}${campusId}/productMargins/_targets`, { targets });
	simulate = async (campusId: number, articleId: number): Promise<AxiosResponse<any>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_simulate/${articleId}`);
	expenses = async (campusId: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/productMargins/_expenses`);
	saveExpense = async (campusId: number, data: Record<string, any>): Promise<AxiosResponse<any[]>> =>
		axiosInstance.post(`${BASE}${campusId}/productMargins/_expenses`, data);
	deleteExpense = async (campusId: number, id: number): Promise<AxiosResponse<any[]>> =>
		axiosInstance.delete(`${BASE}${campusId}/productMargins/_expenses/${id}`);

	incidents = async (campusId: number, filters: Record<string, any>): Promise<AxiosResponse<any[]>> =>
		axiosInstance.get(`${BASE}${campusId}/purchaseOrders/_incidents`, { params: filters });
	createIncident = async (campusId: number, orderId: number, data: Record<string, any>, files: File[]): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseOrders/${orderId}/_incident`, this.form(data, files, "files"));
	updateIncident = async (campusId: number, incidentId: number, data: Record<string, any>): Promise<AxiosResponse<any>> =>
		axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/_incident/${incidentId}`, data);
	readReceipt = async (campusId: number, file: File, supplierId: number | null): Promise<AxiosResponse<{ read: any }>> => {
		const body = new FormData();
		body.append("file", file);
		if (supplierId) body.append("supplierId", String(supplierId));
		return axiosInstance.post(`${BASE}${campusId}/purchaseApprovals/_readReceipt`, body);
	};
}
